import { Link } from "react-router-dom";
import { Twitter, Instagram, Mail } from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    company: [
      { name: "About Us", href: "/about" },
      { name: "Blog", href: "/blog" },
    ],
    support: [
      { name: "Help Center", href: "/faqs" },
      { name: "Contact Us", href: "/contact" },
    ],
    legal: [
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms of Service", href: "/terms" },
      { name: "Disclaimer", href: "/disclaimer" },
      { name: "Refund Policy", href: "/refund-policy" },
    ],
    services: [
      { name: "Flight Search", href: "/search" },
      { name: "Pricing Plans", href: "/pricing" },
    ],
  };

  return (
    <footer className="bg-navy-900 text-white border-t border-white/5">
      <div className="container mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-16">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-8">
              <img src="/pantheon-mark.png" alt="Pantheon Points" className="h-12 w-12 rounded-full object-cover" />
              <div className="flex flex-col">
                <span className="font-display font-black uppercase tracking-[0.2em] text-xl text-white">
                  Pantheon
                </span>
                <span className="font-sans text-[10px] font-black tracking-[0.4em] uppercase text-pantheon-teal">
                  Points
                </span>
              </div>
            </Link>
            <p className="text-white/50 mb-8 max-w-sm font-medium italic leading-relaxed">
              Compare award options and cash prices for any route. One search, smarter transfer decisions.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.instagram.com/pantheonpoints/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="p-3 rounded-xl bg-white/5 hover:bg-pantheon-teal hover:text-white transition-all duration-300"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://x.com/PantheonPoints"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                className="p-3 rounded-xl bg-white/5 hover:bg-pantheon-teal hover:text-white transition-all duration-300"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://www.tiktok.com/@pantheonpoints"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="p-3 rounded-xl bg-white/5 hover:bg-pantheon-teal hover:text-white transition-all duration-300"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V9.15a8.16 8.16 0 0 0 4.77 1.52V7.23a4.85 4.85 0 0 1-1-.54z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-display font-bold text-sm uppercase tracking-widest text-white mb-8">Company</h4>
            <ul className="space-y-4">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/40 hover:text-pantheon-teal font-bold italic text-sm transition-colors duration-200">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="font-display font-bold text-sm uppercase tracking-widest text-white mb-8">Support</h4>
            <ul className="space-y-4">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/40 hover:text-pantheon-teal font-bold italic text-sm transition-colors duration-200">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-display font-bold text-sm uppercase tracking-widest text-white mb-8">Legal</h4>
            <ul className="space-y-4">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/40 hover:text-pantheon-teal font-bold italic text-sm transition-colors duration-200">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-display font-bold text-sm uppercase tracking-widest text-white mb-8">Services</h4>
            <ul className="space-y-4">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link to={link.href} className="text-white/40 hover:text-pantheon-teal font-bold italic text-sm transition-colors duration-200">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-20 pt-10 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <p className="text-white/30 text-[10px] font-black uppercase tracking-[0.2em]">
              © {currentYear} Pantheon Points. All rights reserved.
            </p>
            <div className="flex items-center gap-8 text-[10px] font-black uppercase tracking-[0.2em] text-white/40">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-pantheon-teal" />
                <span>support@pantheonpoints.com</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
